import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { PlayLowerthirdButton } from '@app/modules/audit/components/audit-result/PlayLowerthirdButton';
import { LowerthirdType } from '@app/modules/audit/screens/global-reports/lowerthird/types';
import { Seconds, toTimestamp } from '@mi-front/core';
import { theme } from '@mi-front/ui';
import { Duration, MrtTable, NullState } from '@ui/components';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { filterLowerthird } from './utils';
import { Station } from '@app/core/types/stations';

interface ILowerthirdView {
  data: LowerthirdType[];
  loading: boolean;
  station: Station;
}

const LowerthirdView: React.FunctionComponent<ILowerthirdView> = memo((props) => {
  const { data, loading, station } = props;
  const { formatMessage } = useIntl();
  const { search } = useLocation();
  const queryParams = React.useMemo(() => new URLSearchParams(search), [search]);

  const cursor = queryParams.get('cursor');

  if (data && data.length > 0) {
    return (
      <MrtTable<LowerthirdType>
        filterFns={{
          filterLowerthird: filterLowerthird,
        }}
        muiTableBodyRowProps={({ row }) => {
          return {
            sx: {
              backgroundColor: row.original.from === cursor ? theme.aquaTranslucent : 'inherit',
            },
          };
        }}
        globalFilterFn="filterLowerthird"
        columns={[
          {
            id: 'time',
            header: formatMessage({
              id: 'search.dateTime',
              defaultMessage: 'Fecha y hora',
            }),
            enableColumnFilter: false,
            accessorFn: (row) => row.from,
            Cell: ({ row: { original } }) => (
              <div style={{ display: 'flex', gap: '0.375rem' }}>
                <LDate value={original.from} day="2-digit" month="2-digit" year="2-digit" />
                <LTime value={original.from} second={'numeric'} />
              </div>
            ),
            enableSorting: true,
            size: 60,
          },
          {
            id: 'text',
            enableGlobalFilter: true,
            enableSorting: false,
            enableColumnFilter: false,
            accessorFn: (row) => row.text,
            header: formatMessage({
              id: 'audit.lowerthird.title',
              defaultMessage: 'Zocalos',
            }),
            Cell: ({ renderedCellValue }) => <div>{renderedCellValue}</div>,
            size: 200,
          },
          {
            id: 'duration',
            header: formatMessage({
              id: 'audit.lowerthird.duration',
              defaultMessage: 'Duración',
            }),
            Cell: ({ row: { original } }) => (
              <div>
                <Duration
                  seconds={
                    ((toTimestamp(original.to) - toTimestamp(original.from)) / 1000) as Seconds
                  }
                />
              </div>
            ),
            enableSorting: true,
            size: 30,
          },
          {
            id: 'actions',
            header: formatMessage({
              id: 'common.text.actions',
              defaultMessage: 'Acciones',
            }),
            size: 20,
            Cell: ({ row: { original } }) => (
              <PlayLowerthirdButton lowerthird={original} station={station} />
            ),
          },
        ]}
        data={data}
        enableExpandAll={false}
        enableGlobalFilter={true}
        enableFilters={true}
        enableHiding={false}
        enableColumnActions={false}
        state={{
          isLoading: loading,
        }}
      />
    );
  }

  return (
    <NullState className="p-7">
      {loading ? (
        <FormattedMessage
          id="audit.lowerthirdScreen.loading"
          defaultMessage="Cargando zocalos"
          values={{ count: 0 }}
        />
      ) : (
        <FormattedMessage
          id="audit.lowerthirdScreen.noResults"
          defaultMessage="No lowerthirds found"
          values={{ count: 0 }}
        />
      )}
    </NullState>
  );
});

export default LowerthirdView;
